import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, message } from 'antd';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  TEST_ADDRESS_KEYWORD_RULE,
  TestAddressKeywordRuleResponse,
  TestAddressKeywordRuleInput,
  TestAddressKeywordRuleResult,
} from '../graphql/addressKeywordRuleGraphql';

// 规则条件（由抽屉当前表单计算后传入），用于测试改写与外部 API 验证
export type RuleConditions = Omit<
  TestAddressKeywordRuleInput,
  'testStreetAddress' | 'testUnit' | 'testCity' | 'testState' | 'testZipCode'
>;

interface TestRuleModalProps {
  visible: boolean;
  conditions: RuleConditions | null;
  onClose: () => void;
  /** 测试通过时回调，带回通过的测试地址与标准化结果（保存时随规则一起提交） */
  onPass: (testAddress: string, testResult: string) => void;
}

const TestRuleModal: React.FC<TestRuleModalProps> = ({ visible, conditions, onClose, onPass }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [result, setResult] = useState<TestAddressKeywordRuleResult | null>(null);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setResult(null);
    }
  }, [visible, form]);

  const [testRule, { loading }] = useLazyQuery<TestAddressKeywordRuleResponse>(
    TEST_ADDRESS_KEYWORD_RULE,
    { fetchPolicy: 'network-only' }
  );

  const handleTest = async () => {
    if (!conditions) {
      return;
    }
    let values: {
      testStreetAddress: string;
      testUnit?: string;
      testCity: string;
      testState: string;
      testZipCode: string;
    };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const input: TestAddressKeywordRuleInput = {
      ...conditions,
      testStreetAddress: values.testStreetAddress.trim(),
      testUnit: values.testUnit?.trim() || null,
      testCity: values.testCity.trim(),
      testState: values.testState.trim(),
      testZipCode: values.testZipCode.trim(),
    };
    const { data, error } = await testRule({ variables: { input } });
    if (error) {
      message.error(error.message);
      setResult(null);
      return;
    }
    const res = data?.testAddressKeywordRule;
    if (!res) {
      setResult(null);
      return;
    }
    setResult(res);
    if (res.passed && res.standardizedAddress) {
      message.success(t('mapTool.addressKeywordRule.testModal.passMessage') as string);
      onPass(res.testFullAddress, res.standardizedAddress);
    }
  };

  const correctedLine = result
    ? [result.correctedStreetAddress, result.correctedUnit].filter((v) => !!v).join(' ')
    : '';

  return (
    <Modal
      title={t('mapTool.addressKeywordRule.testModal.title') as string}
      open={visible}
      onCancel={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('components.button.close')}</Button>
          <Button type="primary" loading={loading} onClick={handleTest}>
            {t('mapTool.addressKeywordRule.testModal.buttonTest')}
          </Button>
        </div>
      }
    >
      <p className="text-gray-500 mb-3 text-sm">
        {t('mapTool.addressKeywordRule.testModal.hint')}
      </p>
      <Form form={form} layout="vertical">
        <Form.Item
          name="testStreetAddress"
          label={t('mapTool.addressKeywordRule.testModal.labelStreetAddress')}
          rules={[{ required: true, message: t('mapTool.addressKeywordRule.testModal.ruleStreetAddressRequired') as string }]}
        >
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item name="testUnit" label={t('mapTool.addressKeywordRule.testModal.labelUnit')}>
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item
          name="testCity"
          label={t('mapTool.addressKeywordRule.testModal.labelCity')}
          rules={[{ required: true, message: t('mapTool.addressKeywordRule.testModal.ruleCityRequired') as string }]}
        >
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item
          name="testState"
          label={t('mapTool.addressKeywordRule.testModal.labelState')}
          rules={[{ required: true, message: t('mapTool.addressKeywordRule.testModal.ruleStateRequired') as string }]}
        >
          <Input maxLength={50} />
        </Form.Item>
        <Form.Item
          name="testZipCode"
          label={t('mapTool.addressKeywordRule.testModal.labelZipcode')}
          rules={[{ required: true, message: t('mapTool.addressKeywordRule.testModal.ruleZipcodeRequired') as string }]}
        >
          <Input maxLength={10} />
        </Form.Item>
      </Form>

      {result && (
        <div
          className={`mt-2 px-3 py-2 rounded text-sm border ${
            result.passed
              ? 'bg-green-50 text-green-600 border-green-200'
              : 'bg-red-50 text-red-600 border-red-200'
          }`}
        >
          <div>
            {result.passed
              ? t('mapTool.addressKeywordRule.testModal.passed')
              : `${t('mapTool.addressKeywordRule.testModal.notPassed')}${result.reason ? `: ${result.reason}` : ''}`}
          </div>
          <div className="mt-1 text-gray-500">
            {`${t('mapTool.addressKeywordRule.testModal.fullAddress')}: ${result.testFullAddress}`}
          </div>
          {result.matched && (
            <div className="mt-1 text-gray-500">
              {`${t('mapTool.addressKeywordRule.testModal.corrected')}: ${correctedLine}`}
            </div>
          )}
          {result.passed && result.standardizedAddress && (
            <div className="mt-1 text-gray-500">
              {`${t('mapTool.addressKeywordRule.testModal.standardized')}: ${result.standardizedAddress}`}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default TestRuleModal;
